import React, { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Form from 'carbon-react/lib/components/form';
import Button from 'carbon-react/lib/components/button';
import Icon from 'carbon-react/lib/components/icon';
import { IconType } from 'carbon-react/lib/components/icon/icon-type';
import Message from 'carbon-react/lib/components/message';

import { ActionDialogWizardProps, DialogWizardScreen } from '../shared/dialog-wizard.types';
import { DialogWizardBase } from '../shared/dialog-wizard-base';

import Typography from 'carbon-react/lib/components/typography';

import styles from '../shared/dialog-wizard.scss';

const IconTitle = ({ icon, title, subtitle }: { icon: { type: IconType; color: string }; title: ReactNode; subtitle?: ReactNode }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
      <Icon type={icon.type} color={icon.color} />
      <Typography variant="h1" mb={0}>
        {title}
      </Typography>
    </div>
    {subtitle && (
      <Typography variant="p" fontSize="14px" mb={0} color="var(--colorsUtilityYin065)">
        {subtitle}
      </Typography>
    )}
  </div>
);

const ThingsToKnowSection = ({ items, title, dataTestId }: { items: ReactNode[]; title?: ReactNode; dataTestId: string }) => (
  <div className={styles.thingsToKnow} data-testid={`${dataTestId}-things-to-know`}>
    <Typography fontWeight="500" fontSize="14px" mb={0}>
      {title || (
        <><FormattedMessage id="common.things_to_know" defaultMessage="Things to know" />{':'}</>
      )}
    </Typography>
    <ul className={styles.thingsToKnowList}>
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  </div>
);

/**
 * A dialog wizard for standard confirm actions.
 * Displays a confirm screen with Cancel/Confirm buttons,
 * transitions through loading, success, and error states.
 */
export const ActionDialogWizard = ({
  ariaLabel,
  isOpen,
  onClose,
  onConfirm,
  screen,
  title,
  subtitle,
  confirmTitle,
  confirmDescription,
  loadingMessage,
  successMessage,
  errorMessage,
  confirmButtonText,
  cancelButtonText,
  closeButtonText,
  modalSize,
  icon,
  thingsToKnow,
  thingsToKnowTitle,
  dataTestId = 'action-dialog-wizard'
  // eslint-disable-next-line complexity
}: ActionDialogWizardProps) => {
  const intl = useIntl();

  const defaultConfirmText = intl.formatMessage({
    id: 'common.confirm',
    defaultMessage: 'Confirm'
  });

  const defaultCancelText = intl.formatMessage({
    id: 'common.cancel',
    defaultMessage: 'Cancel'
  });

  const defaultErrorTitle = intl.formatMessage({
    id: 'common.something_went_wrong',
    defaultMessage: 'Something went wrong'
  });

  const defaultErrorMessage = intl.formatMessage({
    id: 'predict.create_forecast.try_again',
    defaultMessage: 'Try again'
  });

  const composedTitle = icon ? (
    <IconTitle icon={icon} title={title} subtitle={subtitle} />
  ) : undefined;

  const confirmScreen = (
    <Form
      onSubmit={() => null}
      leftSideButtons={
        <Button onClick={onClose} buttonType="secondary" data-testid={`${dataTestId}-cancel-button`}>
          {cancelButtonText || defaultCancelText}
        </Button>
      }
      saveButton={
        <Button onClick={onConfirm} buttonType="primary" data-testid={`${dataTestId}-confirm-button`}>
          {confirmButtonText || defaultConfirmText}
        </Button>
      }
      stickyFooter
    >
      {screen === DialogWizardScreen.ERROR && (
        <div data-testid={`${dataTestId}-error-message`} style={{ marginBottom: '16px' }}>
          <Message variant="error" open showCloseIcon={false} title={defaultErrorTitle}>
            {errorMessage || defaultErrorMessage}
          </Message>
        </div>
      )}
      <>
        {confirmTitle && (
          <Typography fontWeight="bold" fontSize="14px" mb={0}>
            {confirmTitle}
          </Typography>
        )}
        {confirmDescription && (
          <Typography fontSize="14px" color="rgba(0, 0, 0, 0.65)" mb={0}>
            {confirmDescription}
          </Typography>
        )}
      </>
      {thingsToKnow && thingsToKnow.length > 0 && (
        <ThingsToKnowSection items={thingsToKnow} title={thingsToKnowTitle} dataTestId={dataTestId} />
      )}
    </Form>
  );

  return (
    <DialogWizardBase
      ariaLabel={ariaLabel || (typeof title === 'string' ? title : undefined)}
      isOpen={isOpen}
      onClose={onClose}
      screen={screen}
      title={composedTitle || title}
      subtitle={composedTitle ? undefined : subtitle}
      loadingMessage={loadingMessage}
      successMessage={successMessage}
      closeButtonText={closeButtonText}
      modalSize={modalSize}
      dataTestId={dataTestId}
    >
      {confirmScreen}
    </DialogWizardBase>
  );
};
